/**
 * Carteira: apolices, emissao e comissoes.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams, type Money, type PartyRef } from './comum';
import type { DocumentDownload } from './documents';

import { api, requestPage } from '../client';

export type PolicyStatus = 'vigente' | 'vencida' | 'cancelada';

export interface Policy {
  id: string;
  number: number;
  external_number: string;
  operation_id: string;
  policyholder: PartyRef;
  broker: PartyRef | null;
  product: PartyRef;
  modality: PartyRef;
  issuer: PartyRef;
  insured_amount: Money;
  premium: Money;
  applied_rate_bps: number;
  commission_bps: number;
  coverage_start: string;
  coverage_end: string;
  status: PolicyStatus;
  days_to_expiry: number;
  cancel_reason: string;
  cancelled_at: string | null;
  issued_label: string;
  issued_at: string;
  /** Endossos aprovados (coluna "Contem endosso?"). */
  endorsement_count?: number;
  /** Documento com arquivo da apolice. Nulo deixa o "Baixar apolice" indisponivel. */
  document?: { id: string; title: string } | null;
  /**
   * Percentual decorrido da vigencia, em pontos-base ("Vigencia" da aba Apolices do
   * segurado, P-SEG-2). Regra sem fonte (SEG-037): vem sempre nulo.
   */
  elapsed_bps?: number | null;

  // Apolice completa (P-APO-1). Opcionais para que a resposta de antes continue
  // valida nos testes e na fila de renovacao.
  /** Numero de 16 digitos da cotacao de origem. */
  proposal_number?: string | null;
  insured?: PartyRef | null;
  /** Fotografia das partes como estavam na emissao. */
  parties?: PolicyParties;
  big_risk?: boolean;
  monitoring?: boolean;
  step_in?: boolean;
  coinsurance_type?: PolicyCoinsuranceType | null;
  has_coinsurance?: boolean;
  /** "Ref. da Seguradora". */
  insurer_reference?: string;
  principal_premium?: Money | null;
  complementary_premium?: Money | null;
  complementary_modality?: PartyRef | null;
  complementary_coverage_start?: string | null;
  complementary_coverage_end?: string | null;
  complementary_insured_amount?: Money | null;
  /** IS na emissao; o endosso de valor muda a IS atual. */
  original_insured_amount?: Money;
  insured_amount_changed?: boolean;
  /** Prazo da vigencia principal como a cotacao gravou. */
  term_days?: number | null;
  installments_count?: number;
  nominated_broker_id?: string | null;
  nominated_to_broker?: boolean;
  created_at?: string;
  updated_at?: string;

  // Laudo, numero longo, ramo e versao assinada (P-APO-2). Opcionais pelo mesmo
  // motivo: a resposta de antes continua valida.
  /** Numero longo, como a seguradora o da. Nulo e "nao informado". */
  long_number?: string | null;
  /** Ramo SUSEP e processo SUSEP na emissao. Nulos nas apolices de antes. */
  branch_code?: string | null;
  branch_name?: string | null;
  susep_process_code?: string | null;
  /** Versao do documento da apolice. Nulo e "nao informado". */
  document_signature_status?: IssuanceReportSignatureStatus | null;
  document_signed_at?: string | null;
  /** Se a apolice tem laudo de emissao e, se nao tem, por que. */
  issuance_report?: IssuanceReportAvailability;

  // Documento da apolice e filial da seguradora (P-MIN-2).
  policy_document?: PolicyDocumentState;
  insurer_branch?: { id: string; name: string; city: string; state: string } | null;
}

// --- Laudo de emissao (P-APO-2) ------------------------------------------------

export type IssuanceReportSignatureStatus = 'aguardando_assinatura' | 'assinada';

export interface IssuanceReportAvailability {
  available: boolean;
  reason: string;
}

/** Endereco temporario do laudo montado sob pedido. */
export interface IssuanceReportLink {
  url: string;
  filename: string;
  format: 'pdf' | 'docx';
  expires_at: string;
  /** Modelo que montou o laudo (P-MIN-3). Opcional: a resposta de antes continua valida. */
  template?: IssuanceReportTemplateUsed;
}

/**
 * Modelo do laudo de emissao (P-MIN-3): o publicado pela empresa em Templates ou o
 * neutro do sistema, com os campos do modelo nulos.
 */
export interface IssuanceReportTemplateUsed {
  source: 'empresa' | 'sistema';
  id: string | null;
  key: string | null;
  name: string | null;
  version: number | null;
}

// --- Apolice completa (P-APO-1) ----------------------------------------------

export type PolicyCoinsuranceType = 'cedido' | 'aceito';

/** Situacoes do filtro da lista, com as do OnPoint. */
export type PolicyListStatus =
  PolicyStatus | 'emitida' | 'aguardando_emissao' | 'rejeitada';

export interface PolicyPartyAddress {
  zip_code: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
}

export interface PolicyParty {
  party_id: string | null;
  legal_name: string;
  trade_name: string;
  /** Nome social, que so o endosso neutro informa (P-END-1). */
  social_name?: string;
  /** CPF ou CNPJ so com digitos. */
  document: string;
  susep_code: string;
  address: PolicyPartyAddress;
  /** emissao, carga (apolices de antes) ou endosso. */
  source: 'emissao' | 'carga' | 'endosso';
  captured_at: string;
}

export interface PolicyParties {
  policyholder: PolicyParty | null;
  insured: PolicyParty | null;
  broker: PolicyParty | null;
}

export interface PolicyCoverage {
  id: string;
  kind: 'basica' | 'adicional';
  coverage_id: string | null;
  name: string;
  position: number;
  insured_amount: Money | null;
  /** Nulo e "nao informado": a distribuicao do premio e regra pendente. */
  premium: Money | null;
  coverage_start: string | null;
  term_days: number | null;
  coverage_end: string | null;
  source: string;
}

export interface PolicyCoinsurer {
  id: string;
  insurer_id: string | null;
  name: string;
  document: string;
  share_bps: number;
  is_leader: boolean;
}

export type PolicyInstallmentStatus = 'em_aberto' | 'paga' | 'vencida' | 'cancelada';

export interface PolicyInstallment {
  id: string;
  number: number;
  amount: Money;
  interest: Money;
  due_on: string;
  status: PolicyInstallmentStatus;
  paid_on: string | null;
  billet_document_id: string | null;
  billet_number: string;
  source: 'manual' | 'cotacao' | 'integracao';
  created_label: string;
  updated_label: string;
  updated_at: string;
}

export type PolicyBilletMode = 'desligado' | 'anexo';

export interface PolicyDetail extends Policy {
  coverages: PolicyCoverage[];
  coinsurers: PolicyCoinsurer[];
  installments: PolicyInstallment[];
  installments_total: Money;
  single_billet: boolean;
  /** Valor da comissao gravado na emissao. Nulo sem repasse. */
  commission_amount: Money | null;
  renewal_status: string | null;
  billet_mode: PolicyBilletMode;
}

export interface PolicyInstallmentInput {
  number: number;
  amount: Money;
  interest?: Money;
  due_on: string;
  status?: PolicyInstallmentStatus;
  paid_on?: string | null;
  billet_document_id?: string | null;
  billet_number?: string;
}

export interface PolicyInstallmentsMeta {
  single_billet: boolean;
  total: Money;
  billet_mode: PolicyBilletMode;
  count: number;
}

export interface PolicyBillets {
  billet_mode: PolicyBilletMode;
  available: boolean;
  /** Por que nao ha boleto, quando nao ha. */
  reason: string;
  single_billet: boolean;
  billets: {
    document_id: string;
    number: string;
    installment_numbers: number[];
    url: string;
    filename: string;
    expires_at: string;
  }[];
}

export type PolicyArchiveKind =
  | 'apolice'
  | 'endosso'
  | 'boleto'
  | 'laudo_emissao'
  | 'certificado_cancelamento'
  | 'outro';

export interface PolicyHistoryDocument {
  id: string;
  document_id: string;
  archive_kind: PolicyArchiveKind;
  title: string;
  filename: string;
  created_label: string;
  created_at: string;
}

export interface PolicyHistoryEntry {
  id: string;
  event_kind:
    'emissao' | 'endosso' | 'cancelamento' | 'renovacao' | 'alteracao' | 'carga';
  summary: string;
  /** Fotografia da apolice no movimento, em centavos e pontos-base. */
  snapshot: Record<string, unknown>;
  endorsement_id: string | null;
  created_label: string;
  created_at: string;
  accepted_label: string;
  accepted_at: string | null;
  documents: PolicyHistoryDocument[];
}

export interface PolicyEndorsement {
  id: string;
  number: number;
  kind: string;
  reason: string;
  status: string;
  amount_delta: Money | null;
  new_coverage_end: string | null;
  requested_label: string;
  decided_label: string;
  decided_at: string | null;
  created_at: string;
}

export interface PolicySettings {
  number_mode: 'informado' | 'automatico';
  number_prefix: string;
  billet_mode: PolicyBilletMode;
  show_pending_issuance: boolean;
  updated_label?: string;
  updated_at?: string | null;
}

/** Operacao em emissao mostrada na lista como "Aguardando Emissao" ("S/N"). */
export interface PolicyPendingIssuance {
  operation_id: string;
  operation_number: number;
  proposal_number: string | null;
  status: 'aguardando_emissao';
  policyholder: PartyRef;
  policyholder_document: string;
  insured: PartyRef | null;
  broker: PartyRef | null;
  modality: PartyRef;
  insured_amount: Money;
  premium: Money;
  coverage_start: string | null;
  coverage_end: string | null;
  big_risk: boolean;
  monitoring: boolean;
  step_in: boolean;
  created_at: string;
}

export interface PolicyIssuanceDocuments {
  required: boolean;
  can_issue: boolean;
  missing: {
    id: string;
    number: number;
    group: string;
    title: string;
    status: string;
  }[];
}

export interface PolicyFileLink {
  url: string;
  filename: string;
  expires_at: string;
}

export interface IssuanceInput {
  insurer_id?: string | null;
  bank_id?: string | null;
  external_number?: string;
  coverage_start?: string | null;
  coverage_end?: string | null;
  /** "Ref. da Seguradora" (P-APO-1). */
  insurer_reference?: string;
  /** Numero longo, quando a seguradora ja o deu (P-APO-2). */
  long_number?: string;
  /** Filial da seguradora que emite (P-MIN-2). */
  insurer_branch_id?: string | null;
}

export type CommissionStatus = 'a_receber' | 'recebida' | 'paga' | 'cancelada';

export interface Commission {
  id: string;
  /** Nulo na comissao lancada a mao. */
  policy_id: string | null;
  manual_reason: string;
  policy_number: number;
  /** Vazios no lancamento manual, que nao tem apolice. */
  policyholder_label: string;
  issuer_label: string;
  broker_label: string;
  base: Money;
  rate_bps: number;
  amount: Money;
  competence: string;
  status: CommissionStatus;
  settled_label: string;
  settled_at: string | null;
  notes: string;
  created_at: string;
  /** Endosso ligado a comissao lancada a mao (P-END-2). */
  endorsement_id?: string | null;
}

export interface CommissionTotals {
  pending: Money;
  received: Money;
  paid: Money;
}

export const policies = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Policy>(`/api/v1/policies${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<Policy>(`/api/v1/policies/${id}`, signal),
  issue: (operationId: string, input: IssuanceInput) =>
    api.post<Policy>(`/api/v1/operations/${operationId}/issuance`, input),
  /**
   * Endereco temporario do documento da apolice ("Baixar apolice"): o gerado do modelo
   * vinculado (P-MIN-2) ou, sem ele, o arquivo ligado a apolice.
   */
  document: (
    id: string,
    format?: 'pdf' | 'docx',
    version?: 'assinada' | 'original',
  ) => {
    // A versao assinada sai por padrao quando existe (P-MIN-4); a original, a pedido.
    const parametros = new URLSearchParams();
    if (format) parametros.set('format', format);
    if (version) parametros.set('version', version);
    const consulta = parametros.toString();
    return api.get<PolicyDocumentDownload>(
      `/api/v1/policies/${id}/document${consulta ? `?${consulta}` : ''}`,
    );
  },
  /** Gera de novo o documento da emissao (P-MIN-2). */
  regenerateDocument: (id: string) =>
    api.post<Policy>(`/api/v1/policies/${id}/document`, {}),

  // Apolice completa (P-APO-1).
  detail: (id: string, signal?: AbortSignal) =>
    api.get<PolicyDetail>(`/api/v1/policies/${id}`, signal),
  exportAll: (params: ListParams) =>
    api.get<Policy[]>(`/api/v1/policies/export${query(params)}`),
  pendingIssuance: (params: ListParams, signal?: AbortSignal) =>
    requestPage<PolicyPendingIssuance>(
      `/api/v1/policies/pending-issuance${query(params)}`,
      signal,
    ),
  settings: (signal?: AbortSignal) =>
    api.get<PolicySettings>('/api/v1/policies/settings', signal),
  updateSettings: (input: PolicySettings) =>
    api.put<PolicySettings>('/api/v1/policies/settings', input),
  history: (id: string, signal?: AbortSignal) =>
    api.get<PolicyHistoryEntry[]>(`/api/v1/policies/${id}/history`, signal),
  linkHistoryDocument: (
    id: string,
    historyId: string,
    input: { document_id: string; archive_kind: PolicyArchiveKind },
  ) =>
    api.post<PolicyHistoryDocument>(
      `/api/v1/policies/${id}/history/${historyId}/documents`,
      input,
    ),
  endorsements: (id: string, signal?: AbortSignal) =>
    api.get<PolicyEndorsement[]>(`/api/v1/policies/${id}/endorsements`, signal),
  installments: (id: string, signal?: AbortSignal) =>
    requestPage<PolicyInstallment>(`/api/v1/policies/${id}/installments`, signal),
  replaceInstallments: (id: string, installments: PolicyInstallmentInput[]) =>
    api.put<PolicyInstallment[]>(`/api/v1/policies/${id}/installments`, {
      installments,
    }),
  /** Os boletos da apolice; com o documento, so o boleto dele (R5-M21). */
  billets: (id: string, documentId?: string, signal?: AbortSignal) =>
    api.get<PolicyBillets>(
      `/api/v1/policies/${id}/billets${documentId ? `?document_id=${encodeURIComponent(documentId)}` : ''}`,
      signal,
    ),
  file: (id: string, documentId: string) =>
    api.get<PolicyFileLink>(`/api/v1/policies/${id}/files/${documentId}`),
  lastEndorsementDocument: (id: string) =>
    api.get<PolicyFileLink>(`/api/v1/policies/${id}/documents/last-endorsement`),
  issuanceDocuments: (operationId: string, signal?: AbortSignal) =>
    api.get<PolicyIssuanceDocuments>(
      `/api/v1/operations/${operationId}/issuance-documents`,
      signal,
    ),

  // Laudo de emissao (P-APO-2): montado sob pedido, da fotografia da emissao.
  issuanceReport: (id: string, format?: 'pdf' | 'docx') =>
    api.get<IssuanceReportLink>(
      `/api/v1/policies/${id}/issuance-report${format ? `?format=${format}` : ''}`,
    ),
};

/**
 * Os totais do repasse vem em `meta`, calculados no banco. Somar apenas o que
 * esta na pagina mostraria um numero que muda ao virar a pagina.
 */
export async function listCommissions(
  params: ListParams,
  signal?: AbortSignal,
): Promise<{
  items: Commission[];
  total: number;
  limit: number;
  offset: number;
  totals: CommissionTotals;
}> {
  const page = await requestPage<Commission>(
    `/api/v1/commissions${query(params)}`,
    signal,
  );
  const totals = (page.meta.totals as CommissionTotals | undefined) ?? {
    pending: { amount: 0, currency: 'BRL', exponent: 2 },
    received: { amount: 0, currency: 'BRL', exponent: 2 },
    paid: { amount: 0, currency: 'BRL', exponent: 2 },
  };
  return { ...page, totals };
}

export interface ManualCommissionInput {
  broker_id: string;
  base: Money;
  rate_bps: number;
  competence?: string;
  manual_reason: string;
  notes?: string;
  /** Endosso aplicado a que a comissao se liga (P-END-2). So a ligacao. */
  endorsement_id?: string | null;
}

export const commissions = {
  list: listCommissions,
  settle: (id: string, status: CommissionStatus) =>
    api.post<Commission>(`/api/v1/commissions/${id}/settlement`, { status }),
  launch: (input: ManualCommissionInput) =>
    api.post<Commission>('/api/v1/commissions', input),
};

// --- Documento da apolice (P-MIN-2) ----------------------------------------------

/**
 * Estado do documento da apolice: gerado do modelo vinculado, nao gerado (com o
 * motivo) ou sem registro (apolice emitida antes do documento da apolice).
 */
export interface PolicyDocumentState {
  status: 'gerado' | 'nao_gerado' | 'sem_registro';
  reason_code: string | null;
  reason: string | null;
  trigger: 'emissao' | 'regeracao' | null;
  credit_group: string | null;
  /** O grupo de credito so emite com modelo vinculado e prova aprovada. */
  locked_group: boolean;
  template_name: string | null;
  template_version: number | null;
  warnings: string[];
  recorded_at: string | null;
  recorded_label: string | null;
  document_id: string | null;
  filename: string | null;
  pdf_ready: boolean;
  pdf_available: boolean;
  generated_at: string | null;
  /** A versao assinada do documento vigente ja voltou do provedor (P-MIN-4). */
  signed?: boolean;
  signed_at?: string | null;
  /** Situacao do ultimo pedido de assinatura ao provedor; nula sem pedido. */
  signature_status?:
    | 'pendente'
    | 'assinado'
    | 'recusado'
    | 'expirado'
    | 'cancelado'
    | 'erro'
    | 'encerrado_incompleto'
    | null;
  signature_request_id?: string | null;
}

export interface PolicyDocumentDownload extends DocumentDownload {
  /** gerado: do modelo vinculado; anexado: o arquivo ligado a apolice. */
  source?: 'gerado' | 'anexado';
  format?: 'pdf' | 'docx' | null;
  /** Versao entregue do documento gerado (P-MIN-4). */
  version?: 'assinada' | 'original' | null;
  signed_at?: string | null;
}
